import React, { useEffect, useMemo, useState } from "react";
import api from "../api";
import { toast } from "sonner";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { browserGeocode } from "../lib/browserGeocode";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  CartesianGrid, Line, ComposedChart,
} from "recharts";
import {
  Plus, FileCsv, Printer, PencilSimple, Trash, Funnel, Gear, X,
  CurrencyDollar, TrendUp, TrendDown, ChartBar, Stack, Truck, Receipt, Scales,
} from "@phosphor-icons/react";

const FREQ_PER_MONTH = { weekly: 4.33, biweekly: 2.17, monthly: 1, twice_weekly: 8.66, three_weekly: 12.99 };

const emptyForm = {
  route_id: "", route_name: "", date: new Date().toISOString().slice(0, 10),
  driver_id: "", driver_name: "", truck: "",
  disposal_ticket: "",
  tons_disposed: 0, disposal_cost: "",
  fuel_expense: 0, labor_hours: 0, labor_cost: "",
  miles_driven: 0, stops_completed: 0, yards_picked_up: 0,
  route_revenue: 0, notes: "",
};

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtPct = (n) => `${(Number(n || 0) * 100).toFixed(1)}%`;

export default function Reports() {
  const [reports, setReports] = useState([]);
  const [summary, setSummary] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [settings, setSettings] = useState({ tipping_rate: 65, hourly_rate: 28 });

  const [filters, setFilters] = useState({ date_from: "", date_to: "", route_id: "", driver_id: "" });
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [showSettings, setShowSettings] = useState(false);
  // Actuals toggle — auto ON when any disposal tickets exist for the range.
  const [useActuals, setUseActuals] = useState(false);
  const [userForcedActuals, setUserForcedActuals] = useState(false);

  async function loadFilters() {
    const [r, d, s] = await Promise.all([api.get("/routes"), api.get("/drivers"), api.get("/settings")]);
    setRoutes(r.data); setDrivers(d.data); setSettings(s.data);
  }
  async function loadData() {
    const params = {};
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    if (filters.route_id) params.route_id = filters.route_id;
    if (filters.driver_id) params.driver_id = filters.driver_id;
    const [list, sum] = await Promise.all([
      api.get("/reports", { params }),
      api.get("/reports/summary", { params }),
    ]);
    setReports(list.data);
    setSummary(sum.data);
    // Auto-mode: enable actuals iff any tickets exist AND user hasn't manually chosen.
    if (!userForcedActuals) {
      setUseActuals((sum.data?.actual_ticket_count || 0) > 0);
    }
  }
  useEffect(() => { loadFilters(); }, []);
  useEffect(() => { loadData(); }, [filters]);

  const projectedDisposal = useMemo(() => {
    const t = Number(form.tons_disposed || 0);
    return form.disposal_cost === "" || form.disposal_cost == null ? t * settings.tipping_rate : Number(form.disposal_cost);
  }, [form.tons_disposed, form.disposal_cost, settings.tipping_rate]);
  const projectedLabor = useMemo(() => {
    const h = Number(form.labor_hours || 0);
    return form.labor_cost === "" || form.labor_cost == null ? h * settings.hourly_rate : Number(form.labor_cost);
  }, [form.labor_hours, form.labor_cost, settings.hourly_rate]);
  const projectedExpense = projectedDisposal + Number(form.fuel_expense || 0) + projectedLabor;
  const projectedProfit = Number(form.route_revenue || 0) - projectedExpense;

  function openCreate() {
    setEditing(null); setForm(emptyForm); setShowForm(true);
  }
  function openEdit(r) {
    setEditing(r);
    setForm({
      route_id: r.route_id || "", route_name: r.route_name || "",
      date: r.date, driver_id: r.driver_id || "", driver_name: r.driver_name || "",
      truck: r.truck || "", disposal_ticket: r.disposal_ticket || "",
      tons_disposed: r.tons_disposed || 0, disposal_cost: r.disposal_cost ?? "",
      fuel_expense: r.fuel_expense || 0, labor_hours: r.labor_hours || 0, labor_cost: r.labor_cost ?? "",
      miles_driven: r.miles_driven || 0, stops_completed: r.stops_completed || 0,
      yards_picked_up: r.yards_picked_up || 0, route_revenue: r.route_revenue || 0,
      notes: r.notes || "",
    });
    setShowForm(true);
  }
  function onPickRoute(rid) {
    const r = routes.find((x) => x.id === rid);
    const label = r ? `#${r.route_number}${r.name ? ` · ${r.name}` : ""}` : "";
    setForm((f) => ({ ...f, route_id: rid, route_name: label || f.route_name }));
  }
  function onPickDriver(did) {
    const d = drivers.find((x) => x.id === did);
    setForm((f) => ({ ...f, driver_id: did, driver_name: d?.name || f.driver_name }));
  }

  async function submit(e) {
    e.preventDefault();
    const payload = {
      ...form,
      tons_disposed: Number(form.tons_disposed) || 0,
      disposal_cost: form.disposal_cost === "" ? null : Number(form.disposal_cost),
      fuel_expense: Number(form.fuel_expense) || 0,
      labor_hours: Number(form.labor_hours) || 0,
      labor_cost: form.labor_cost === "" ? null : Number(form.labor_cost),
      miles_driven: Number(form.miles_driven) || 0,
      stops_completed: Number(form.stops_completed) || 0,
      yards_picked_up: Number(form.yards_picked_up) || 0,
      route_revenue: Number(form.route_revenue) || 0,
    };
    try {
      if (editing) {
        await api.put(`/reports/${editing.id}`, payload);
        toast.success("Report updated");
      } else {
        await api.post("/reports", payload);
        toast.success("Report saved");
      }
      setShowForm(false);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  }

  async function del(r) {
    if (!window.confirm(`Delete report for ${r.route_name} on ${r.date}?`)) return;
    await api.delete(`/reports/${r.id}`);
    toast.success("Deleted");
    loadData();
  }

  async function saveSettings(e) {
    e.preventDefault();
    try {
      const s = { ...settings };
      if (s.yard_address && s.yard_lat == null) {
        const hit = await browserGeocode(s.yard_address);
        if (hit) { s.yard_lat = hit.lat; s.yard_lng = hit.lng; }
      }
      if (s.end_address && s.end_lat == null) {
        const hit = await browserGeocode(s.end_address);
        if (hit) { s.end_lat = hit.lat; s.end_lng = hit.lng; }
      }
      const { data } = await api.put("/settings", {
        tipping_rate: Number(s.tipping_rate) || 0,
        hourly_rate: Number(s.hourly_rate) || 0,
        yard_name: s.yard_name || "ZMEX Yard",
        yard_address: s.yard_address || "",
        yard_lat: s.yard_lat ?? null,
        yard_lng: s.yard_lng ?? null,
        end_name: s.end_name || "ZMEX Yard",
        end_address: s.end_address || "",
        end_lat: s.end_lat ?? null,
        end_lng: s.end_lng ?? null,
      });
      setSettings(data);
      if (settings.yard_address && !data.yard_lat) {
        toast.warning("Could not geocode the Yard address. Please double-check it.");
      } else if (settings.end_address && !data.end_lat) {
        toast.warning("Could not geocode the End address. Please double-check it.");
      } else {
        toast.success("Settings saved");
      }
      setShowSettings(false);
      loadData();
    } catch {
      toast.error("Failed to save");
    }
  }

  function exportCsv() {
    const headers = [
      "date", "route_name", "driver_name", "truck", "disposal_ticket",
      "tons_disposed", "disposal_cost", "fuel_expense", "labor_hours", "labor_cost",
      "miles_driven", "stops_completed", "yards_picked_up",
      "route_revenue", "total_expense", "net_profit", "profit_margin",
      "cost_per_stop", "revenue_per_stop", "cost_per_yard", "profit_per_yard", "notes",
    ];
    const escape = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = reports.map((r) => headers.map((h) => escape(r[h])).join(","));
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `zmex-hub-reports-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported");
  }

  async function exportCsvActuals() {
    // Fetch tickets scoped to the same date range and export a tickets-based CSV
    // reflecting real disposal cost. Fuel/labor still come from route reports.
    try {
      const params = {};
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const { data } = await api.get("/disposal-tickets", { params });
      const headers = [
        "dump_date", "facility", "ticket_number", "tons", "price_per_ton", "total_cost",
        "route_id", "notes",
      ];
      const escape = (v) => {
        const s = v == null ? "" : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const rows = (data.tickets || []).map((r) => headers.map((h) => escape(r[h])).join(","));
      // Summary rows at the top for a P&L snapshot using actuals
      const s = summary || {};
      const summaryRows = [
        `# ZMEX Hub — Actuals P&L (${filters.date_from || "all"} to ${filters.date_to || "now"})`,
        `# Gross Revenue,${s.gross_revenue ?? 0}`,
        `# Actual Disposal Cost (Tickets),${s.actual_disposal_cost ?? 0}`,
        `# Fuel Cost,${s.fuel_cost ?? 0}`,
        `# Labor Cost,${s.labor_cost ?? 0}`,
        `# Total Expense (Actuals),${s.total_expense_actual ?? 0}`,
        `# Net Profit (Actuals),${s.net_profit_actual ?? 0}`,
        `# Profit Margin (Actuals),${((s.profit_margin_actual ?? 0) * 100).toFixed(2)}%`,
        `# Ticket Count,${s.actual_ticket_count ?? 0}`,
        `# Actual Tons,${s.actual_tons_from_tickets ?? 0}`,
        "",
      ];
      const csv = [...summaryRows, headers.join(","), ...rows].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `zmex-hub-reports-actuals-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Actuals CSV exported");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Actuals CSV export failed");
    }
  }

  function exportPdf() {
    window.print();
  }

  const monthlyServiceCount = (freq) => FREQ_PER_MONTH[freq] || 4.33; // exposed for transparency

  return (
    <div className="space-y-5 print:space-y-3" data-testid="reports-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3 print:hidden">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#5C738A]">Analytics</div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-[#012340] mt-1">Reports</h1>
          <p className="text-sm text-[#5C738A] mt-1">Route profitability and operational performance.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setShowSettings(true)} className="px-3 py-2 rounded-sm border border-[#012340]/20 text-sm font-bold text-[#012340] inline-flex items-center gap-1.5 hover:bg-[#012340]/5" data-testid="settings-btn">
            <Gear size={16} weight="bold" /> Settings
          </button>
          <button onClick={exportCsv} className="px-3 py-2 rounded-sm border border-[#012340]/20 text-sm font-bold text-[#012340] inline-flex items-center gap-1.5 hover:bg-[#012340]/5" data-testid="export-csv">
            <FileCsv size={16} weight="bold" /> CSV
          </button>
          <button
            onClick={exportCsvActuals}
            disabled={!summary || (summary.actual_ticket_count || 0) === 0}
            className="px-3 py-2 rounded-sm border border-[#2E7D32]/40 text-sm font-bold text-[#2E7D32] inline-flex items-center gap-1.5 hover:bg-[#2E7D32]/5 disabled:opacity-40 disabled:cursor-not-allowed"
            title={summary?.actual_ticket_count > 0 ? "Export P&L using real Disposal Ticket data" : "No disposal tickets in the selected range"}
            data-testid="export-csv-actuals"
          >
            <Scales size={16} weight="bold" /> CSV (Actuals)
          </button>
          <button onClick={exportPdf} className="px-3 py-2 rounded-sm border border-[#012340]/20 text-sm font-bold text-[#012340] inline-flex items-center gap-1.5 hover:bg-[#012340]/5" data-testid="export-pdf">
            <Printer size={16} weight="bold" /> PDF
          </button>
          <button onClick={openCreate} className="btn-primary" data-testid="report-new-btn">
            <Plus size={16} weight="bold" className="mr-1.5" /> New Entry
          </button>
        </div>
      </div>

      {/* Print header (only visible during print) */}
      <div className="hidden print:block print:mb-4">
        <h1 className="font-heading text-2xl font-black text-[#012340]">ZMEX Hub — Profitability Report</h1>
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#CFAB7A] mt-0.5">A ZMEX Disposal Company</p>
        <p className="text-xs text-[#5C738A]">
          Generated {new Date().toLocaleString()} · Range: {filters.date_from || "all"} → {filters.date_to || "now"}
          {filters.route_id ? ` · Route: ${(() => { const r = routes.find((x) => x.id === filters.route_id); return r ? `#${r.route_number}${r.name ? ` · ${r.name}` : ""}` : ""; })()}` : ""}
          {filters.driver_id ? ` · Driver: ${drivers.find((d) => d.id === filters.driver_id)?.name}` : ""}
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#012340]/10 rounded-sm p-4 print:hidden">
        <div className="flex items-center gap-2 mb-3">
          <Funnel size={16} weight="bold" className="text-[#012340]" />
          <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#5C738A]">Filters</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C738A]">From</label>
            <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className={inputCls} data-testid="filter-from" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C738A]">To</label>
            <input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className={inputCls} data-testid="filter-to" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C738A]">Route</label>
            <select value={filters.route_id} onChange={(e) => setFilters({ ...filters, route_id: e.target.value })} className={inputCls} data-testid="filter-route">
              <option value="">All routes</option>
              {routes.map((r) => <option key={r.id} value={r.id}>#{r.route_number}{r.name ? ` · ${r.name}` : ""}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C738A]">Driver</label>
            <select value={filters.driver_id} onChange={(e) => setFilters({ ...filters, driver_id: e.target.value })} className={inputCls} data-testid="filter-driver">
              <option value="">All drivers</option>
              {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <button onClick={() => setFilters({ date_from: "", date_to: "", route_id: "", driver_id: "" })} className="w-full px-3 py-2 rounded-sm border border-[#012340]/20 text-xs font-bold text-[#012340] hover:bg-[#012340]/5" data-testid="filter-clear">
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Actuals toggle + est vs actual delta line */}
      {summary && (
        <div className="bg-white border border-[#012340]/10 rounded-sm p-3 flex flex-wrap items-center gap-3 print:hidden" data-testid="actuals-toggle-row">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={useActuals}
              onChange={(e) => { setUseActuals(e.target.checked); setUserForcedActuals(true); }}
              className="w-4 h-4 accent-[#012340]"
              data-testid="use-actuals-toggle"
            />
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#012340]">Use Actual Disposal (Tickets)</span>
          </label>
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#5C738A]">
            {(summary.actual_ticket_count || 0) > 0
              ? `${summary.actual_ticket_count} ticket${summary.actual_ticket_count === 1 ? "" : "s"} in range`
              : "No tickets in range"}
          </span>
          <span className="hidden sm:inline text-[#012340]/20">·</span>
          <span className="text-xs text-[#5C738A]" data-testid="actuals-delta-line">
            Est disposal <b className="text-[#012340]">{fmt$(summary.disposal_cost)}</b>
            <span className="mx-2">vs</span>
            Actual <b className="text-[#012340]">{fmt$(summary.actual_disposal_cost)}</b>
            {(summary.actual_ticket_count || 0) > 0 && (
              <span className={`ml-2 font-bold ${((summary.actual_disposal_cost - summary.disposal_cost) >= 0) ? "text-[#B71C1C]" : "text-[#2E7D32]"}`}>
                (Δ {fmt$(summary.actual_disposal_cost - summary.disposal_cost)})
              </span>
            )}
          </span>
          {!userForcedActuals && (
            <span className="ml-auto text-[9px] uppercase tracking-[0.22em] px-2 py-1 rounded-sm bg-[#012340]/5 text-[#5C738A]" data-testid="actuals-auto-badge">Auto</span>
          )}
          {userForcedActuals && (
            <button
              onClick={() => { setUserForcedActuals(false); setUseActuals((summary.actual_ticket_count || 0) > 0); }}
              className="ml-auto text-[9px] uppercase tracking-[0.22em] px-2 py-1 rounded-sm border border-[#012340]/20 text-[#012340] hover:bg-[#012340]/5"
              data-testid="actuals-reset-auto"
            >
              Reset to Auto
            </button>
          )}
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard label="Gross Revenue" value={fmt$(summary?.gross_revenue)} Icon={CurrencyDollar} accent />
        <SummaryCard
          label={useActuals ? "Total Expense (Actuals)" : "Total Expense"}
          value={fmt$(useActuals ? summary?.total_expense_actual : summary?.total_expense)}
          Icon={Receipt}
          sub={`Disp ${fmt$(useActuals ? summary?.actual_disposal_cost : summary?.disposal_cost)}${useActuals ? " (Actual)" : ""} · Fuel ${fmt$(summary?.fuel_cost)} · Labor ${fmt$(summary?.labor_cost)}`}
        />
        <SummaryCard
          label={useActuals ? "Net Profit (Actuals)" : "Net Profit"}
          value={fmt$(useActuals ? summary?.net_profit_actual : summary?.net_profit)}
          Icon={summary && (useActuals ? summary.net_profit_actual : summary.net_profit) >= 0 ? TrendUp : TrendDown}
          positive={summary && (useActuals ? summary.net_profit_actual : summary.net_profit) >= 0}
        />
        <SummaryCard
          label={useActuals ? "Profit Margin (Actuals)" : "Profit Margin"}
          value={fmtPct(useActuals ? summary?.profit_margin_actual : summary?.profit_margin)}
          Icon={ChartBar}
          accent
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniStat label="Stops" value={summary?.total_stops ?? "—"} sub={`${fmt$(summary?.revenue_per_stop)} rev/stop`} />
        <MiniStat label="Yards" value={summary?.total_yards ?? "—"} sub={`${fmt$(summary?.profit_per_yard)} profit/yd³`} />
        <MiniStat label="Tons" value={summary?.total_tons ?? "—"} sub={`Tip $${summary?.tipping_rate}/ton`} />
        <MiniStat label="Miles" value={summary?.total_miles ?? "—"} sub={`${summary?.report_count ?? 0} route-days`} />
      </div>

      {/* Chart */}
      <div className="bg-white border border-[#012340]/10 rounded-sm p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#5C738A]">Revenue · Expense · Profit by day</div>
          <Stack size={16} weight="bold" className="text-[#012340]/40" />
        </div>
        <div style={{ width: "100%", height: 280 }} data-testid="reports-chart">
          <ResponsiveContainer>
            <ComposedChart data={summary?.chart || []} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#012340" strokeOpacity={0.08} vertical={false} />
              <XAxis dataKey="date" tick={{ fill: "#5C738A", fontSize: 11 }} />
              <YAxis tick={{ fill: "#5C738A", fontSize: 11 }} />
              <Tooltip
                contentStyle={{ borderRadius: 2, border: "1px solid rgba(1,35,64,0.15)", fontFamily: "IBM Plex Sans" }}
                formatter={(v) => fmt$(v)}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="revenue" name="Revenue" fill="#CFAB7A" radius={[2, 2, 0, 0]} />
              <Bar dataKey="expense" name="Expense" fill="#012340" radius={[2, 2, 0, 0]} />
              <Line dataKey="profit" name="Net Profit" stroke="#2E7D32" strokeWidth={2.5} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Reports table */}
      <div className="bg-white border border-[#012340]/10 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-[0.2em] text-[#5C738A]">
            <tr className="text-left">
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Route</th>
              <th className="px-4 py-3">Driver</th>
              <th className="px-4 py-3">Truck</th>
              <th className="px-4 py-3">Stops</th>
              <th className="px-4 py-3">Yards</th>
              <th className="px-4 py-3">Tons</th>
              <th className="px-4 py-3">Revenue</th>
              <th className="px-4 py-3">Expense</th>
              <th className="px-4 py-3">Net</th>
              <th className="px-4 py-3">Margin</th>
              <th className="px-4 py-3 print:hidden text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {reports.map((r) => (
              <tr key={r.id} className="even:bg-[#FAFAFA] border-t border-[#012340]/5">
                <td className="px-4 py-3 font-mono text-xs">{r.date}</td>
                <td className="px-4 py-3 font-semibold text-[#012340]">{r.route_name}</td>
                <td className="px-4 py-3">{r.driver_name || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs">{r.truck || "—"}</td>
                <td className="px-4 py-3">{r.stops_completed}</td>
                <td className="px-4 py-3">{r.yards_picked_up}</td>
                <td className="px-4 py-3">{r.tons_disposed}</td>
                <td className="px-4 py-3 font-bold">{fmt$(r.route_revenue)}</td>
                <td className="px-4 py-3">{fmt$(r.total_expense)}</td>
                <td className={`px-4 py-3 font-bold ${r.net_profit >= 0 ? "text-[#2E7D32]" : "text-[#D32F2F]"}`}>{fmt$(r.net_profit)}</td>
                <td className="px-4 py-3">{fmtPct(r.profit_margin)}</td>
                <td className="px-4 py-3 print:hidden text-right">
                  <button onClick={() => openEdit(r)} className="p-1.5 rounded-sm hover:bg-[#012340]/5" data-testid={`report-edit-${r.id}`}><PencilSimple size={16} weight="bold" /></button>
                  <button onClick={() => del(r)} className="p-1.5 rounded-sm hover:bg-[#D32F2F]/10 text-[#D32F2F]" data-testid={`report-del-${r.id}`}><Trash size={16} weight="bold" /></button>
                </td>
              </tr>
            ))}
            {!reports.length && (
              <tr><td colSpan={12} className="px-4 py-10 text-center text-[#5C738A]">No reports for this filter. Click <span className="font-bold">New Entry</span> to add one.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="text-[11px] text-[#5C738A] print:mt-4">
        Service intensity assumes <span className="font-bold">4.33 weeks / month</span> for monthly calculations
        (e.g., weekly = {monthlyServiceCount("weekly")} lifts/mo, twice-weekly = {monthlyServiceCount("twice_weekly")} lifts/mo).
        Disposal computed at <span className="font-bold">${settings.tipping_rate}/ton</span>;
        labor at <span className="font-bold">${settings.hourly_rate}/hr</span>.
      </div>

      {/* Entry form modal */}
      {showForm && (
        <div className="fixed inset-0 z-40 bg-black/40 grid place-items-center p-4" onClick={() => setShowForm(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white w-full max-w-3xl rounded-sm border border-[#012340]/10 max-h-[90vh] overflow-y-auto" data-testid="report-form">
            <div className="sticky top-0 bg-white border-b border-[#012340]/10 px-5 py-4 flex items-center justify-between">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#5C738A]">{editing ? "Edit" : "New"}</div>
                <div className="font-heading text-xl font-bold text-[#012340]">Route Report</div>
              </div>
              <button type="button" onClick={() => setShowForm(false)}><X size={18} weight="bold" /></button>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField label="Route">
                <select className={inputCls} value={form.route_id} onChange={(e) => onPickRoute(e.target.value)} data-testid="rf-route">
                  <option value="">— Pick route —</option>
                  {routes.map((r) => <option key={r.id} value={r.id}>#{r.route_number}{r.name ? ` · ${r.name}` : ""}</option>)}
                </select>
              </FormField>
              <FormField label="Route Name" required>
                <input className={inputCls} required value={form.route_name} onChange={(e) => setForm({ ...form, route_name: e.target.value })} data-testid="rf-name" />
              </FormField>
              <FormField label="Date" required>
                <input type="date" className={inputCls} required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="rf-date" />
              </FormField>

              <FormField label="Driver">
                <select className={inputCls} value={form.driver_id} onChange={(e) => onPickDriver(e.target.value)} data-testid="rf-driver">
                  <option value="">— Pick driver —</option>
                  {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </FormField>
              <FormField label="Truck">
                <input className={inputCls} value={form.truck} onChange={(e) => setForm({ ...form, truck: e.target.value })} placeholder="ZX-101" />
              </FormField>
              <FormField label="Disposal Ticket #">
                <input className={inputCls} value={form.disposal_ticket} onChange={(e) => setForm({ ...form, disposal_ticket: e.target.value })} />
              </FormField>

              <FormField label="Tons Disposed">
                <input type="number" step="0.01" className={inputCls} value={form.tons_disposed} onChange={(e) => setForm({ ...form, tons_disposed: e.target.value })} data-testid="rf-tons" />
              </FormField>
              <FormField label={`Disposal Cost (auto: ${fmt$(Number(form.tons_disposed || 0) * settings.tipping_rate)})`}>
                <input type="number" step="0.01" className={inputCls} placeholder={`auto @ $${settings.tipping_rate}/ton`} value={form.disposal_cost} onChange={(e) => setForm({ ...form, disposal_cost: e.target.value })} data-testid="rf-disposal-cost" />
              </FormField>
              <FormField label="Fuel Expense">
                <input type="number" step="0.01" className={inputCls} value={form.fuel_expense} onChange={(e) => setForm({ ...form, fuel_expense: e.target.value })} data-testid="rf-fuel" />
              </FormField>

              <FormField label="Labor Hours">
                <input type="number" step="0.01" className={inputCls} value={form.labor_hours} onChange={(e) => setForm({ ...form, labor_hours: e.target.value })} data-testid="rf-hours" />
              </FormField>
              <FormField label={`Labor Cost (auto: ${fmt$(Number(form.labor_hours || 0) * settings.hourly_rate)})`}>
                <input type="number" step="0.01" className={inputCls} placeholder={`auto @ $${settings.hourly_rate}/hr`} value={form.labor_cost} onChange={(e) => setForm({ ...form, labor_cost: e.target.value })} data-testid="rf-labor-cost" />
              </FormField>
              <FormField label="Miles Driven">
                <input type="number" step="0.1" className={inputCls} value={form.miles_driven} onChange={(e) => setForm({ ...form, miles_driven: e.target.value })} />
              </FormField>

              <FormField label="Stops Completed">
                <input type="number" className={inputCls} value={form.stops_completed} onChange={(e) => setForm({ ...form, stops_completed: e.target.value })} data-testid="rf-stops" />
              </FormField>
              <FormField label="Yards Picked Up">
                <input type="number" step="0.1" className={inputCls} value={form.yards_picked_up} onChange={(e) => setForm({ ...form, yards_picked_up: e.target.value })} data-testid="rf-yards" />
              </FormField>
              <FormField label="Route Revenue">
                <input type="number" step="0.01" className={inputCls} value={form.route_revenue} onChange={(e) => setForm({ ...form, route_revenue: e.target.value })} data-testid="rf-revenue" />
              </FormField>

              <FormField label="Notes" wide>
                <textarea className={inputCls + " min-h-[60px]"} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </FormField>
            </div>

            <div className="mx-5 my-3 p-3 bg-[#FAFAFA] border border-[#012340]/10 rounded-sm grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <Preview label="Total Expense" value={fmt$(projectedExpense)} />
              <Preview label="Net Profit" value={fmt$(projectedProfit)} positive={projectedProfit >= 0} />
              <Preview label="Profit Margin" value={form.route_revenue > 0 ? fmtPct(projectedProfit / Number(form.route_revenue)) : "—"} />
              <Preview label="Cost / Stop" value={form.stops_completed > 0 ? fmt$(projectedExpense / Number(form.stops_completed)) : "—"} />
            </div>

            <div className="sticky bottom-0 bg-white border-t border-[#012340]/10 px-5 py-3 flex justify-end gap-2">
              <button type="button" className="px-4 py-2 rounded-sm border border-[#012340]/20 text-sm font-bold" onClick={() => setShowForm(false)}>Cancel</button>
              <button type="submit" className="btn-primary" data-testid="report-save">{editing ? "Update" : "Save"}</button>
            </div>
          </form>
        </div>
      )}

      {/* Settings modal */}
      {showSettings && (
        <div className="fixed inset-0 z-40 bg-black/40 grid place-items-center p-4" onClick={() => setShowSettings(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={saveSettings} className="bg-white w-full max-w-lg rounded-sm border border-[#012340]/10 max-h-[90vh] overflow-y-auto" data-testid="settings-form">
            <div className="sticky top-0 bg-white px-5 py-4 border-b border-[#012340]/10 flex items-center justify-between">
              <div className="font-heading text-lg font-bold text-[#012340]">Operational Settings</div>
              <button type="button" onClick={() => setShowSettings(false)}><X size={18} weight="bold" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#5C738A] mb-2">Rates</div>
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="Tipping ($ / ton)" required>
                    <input type="number" step="0.01" required className={inputCls} value={settings.tipping_rate} onChange={(e) => setSettings({ ...settings, tipping_rate: e.target.value })} data-testid="st-tipping" />
                  </FormField>
                  <FormField label="Labor ($ / hr)" required>
                    <input type="number" step="0.01" required className={inputCls} value={settings.hourly_rate} onChange={(e) => setSettings({ ...settings, hourly_rate: e.target.value })} data-testid="st-hourly" />
                  </FormField>
                </div>
              </div>

              <div className="border-t border-[#012340]/10 pt-4">
                <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#5C738A] mb-2">Route Start Point</div>
                <div className="space-y-3">
                  <FormField label="Start Name">
                    <input className={inputCls} value={settings.yard_name || ""} onChange={(e) => setSettings({ ...settings, yard_name: e.target.value })} placeholder="ZMEX Yard" data-testid="st-yard-name" />
                  </FormField>
                  <FormField label="Start Address">
                    <AddressAutocomplete
                      value={settings.yard_address || ""}
                      onChange={(v) => setSettings({ ...settings, yard_address: v, yard_lat: null, yard_lng: null })}
                      onPlace={({ address, lat, lng }) => setSettings({ ...settings, yard_address: address, yard_lat: lat, yard_lng: lng })}
                      className={inputCls}
                      placeholder="Enter route start location"
                      testid="st-yard-address"
                    />
                  </FormField>
                  {settings.yard_lat && settings.yard_lng && (
                    <div className="text-[10px] text-[#2E7D32] font-bold">📍 Pinned at {Number(settings.yard_lat).toFixed(5)}, {Number(settings.yard_lng).toFixed(5)}</div>
                  )}
                </div>
              </div>

              <div className="border-t border-[#012340]/10 pt-4">
                <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#5C738A] mb-2">Route End Point</div>
                <div className="space-y-3">
                  <FormField label="End Name">
                    <input className={inputCls} value={settings.end_name || ""} onChange={(e) => setSettings({ ...settings, end_name: e.target.value })} placeholder="ZMEX Yard" data-testid="st-end-name" />
                  </FormField>
                  <FormField label="End Address">
                    <AddressAutocomplete
                      value={settings.end_address || ""}
                      onChange={(v) => setSettings({ ...settings, end_address: v, end_lat: null, end_lng: null })}
                      onPlace={({ address, lat, lng }) => setSettings({ ...settings, end_address: address, end_lat: lat, end_lng: lng })}
                      className={inputCls}
                      placeholder="Enter route end location"
                      testid="st-end-address"
                    />
                  </FormField>
                  {settings.end_lat && settings.end_lng && (
                    <div className="text-[10px] text-[#2E7D32] font-bold">📍 Pinned at {Number(settings.end_lat).toFixed(5)}, {Number(settings.end_lng).toFixed(5)}</div>
                  )}
                </div>
              </div>

              <p className="text-[11px] text-[#5C738A]">
                Addresses are pinned with Google Maps on save. Rates apply to auto-computed disposal/labor on report entries.
              </p>
            </div>
            <div className="sticky bottom-0 bg-white px-5 py-3 border-t border-[#012340]/10 flex justify-end gap-2">
              <button type="button" onClick={() => setShowSettings(false)} className="px-4 py-2 rounded-sm border border-[#012340]/20 text-sm font-bold">Cancel</button>
              <button type="submit" className="btn-primary" data-testid="settings-save">Save</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full rounded-sm bg-white border border-[#012340]/20 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#CFAB7A]";

function FormField({ label, children, wide, required }) {
  return (
    <div className={wide ? "md:col-span-3" : ""}>
      <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C738A]">{label}{required && <span className="text-[#D32F2F]">*</span>}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function SummaryCard({ label, value, Icon, accent, positive, sub }) {
  return (
    <div className="bg-white border border-[#012340]/10 rounded-sm p-5" data-testid={`summary-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between">
        <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#5C738A]">{label}</div>
        <div className={`w-8 h-8 rounded-sm grid place-items-center ${accent ? "bg-[#CFAB7A] text-[#012340]" : "bg-[#012340] text-[#CFAB7A]"}`}>
          <Icon size={16} weight="bold" />
        </div>
      </div>
      <div className={`kpi-number text-3xl mt-3 ${positive === false ? "text-[#D32F2F]" : positive === true ? "text-[#2E7D32]" : "text-[#012340]"}`}>{value}</div>
      {sub && <div className="text-[10px] text-[#5C738A] mt-1">{sub}</div>}
    </div>
  );
}

function MiniStat({ label, value, sub }) {
  return (
    <div className="bg-white border border-[#012340]/10 rounded-sm p-4">
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C738A]">{label}</div>
      <div className="kpi-number text-2xl text-[#012340] mt-1">{value}</div>
      <div className="text-[10px] text-[#5C738A] mt-0.5">{sub}</div>
    </div>
  );
}

function Preview({ label, value, positive }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C738A]">{label}</div>
      <div className={`font-heading font-bold ${positive === false ? "text-[#D32F2F]" : positive === true ? "text-[#2E7D32]" : "text-[#012340]"}`}>{value}</div>
    </div>
  );
}
